import { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'

import {
  Badge,
  Icon,
  Menu,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow,
  Spinner
} from '@percent/lemonade'
import { formatMoney, Money } from '@percent/utility'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import { useMutation, useQueryList } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { env } from '@percent/partner-dashboard/env'
import { dayJS } from '@percent/partner-dashboard/common/library/utility/date'
import {
  GetVolunteeringTimeLogsListProps,
  VolunteeringTimeLog,
  VolunteeringTimeLogReward,
  VolunteeringTimeLogType
} from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { TimeLogsSearchAndFilters } from './TimeLogsSearchAndFilters/TimeLogsSearchAndFilters'
import { DeleteTimeLogModal } from './DeleteTimeLogModal/DeleteTimeLogModal'
import { DeleteTimeLogErrorModal } from './DeleteTimeLogModal/DeleteTimeLogErrorModal'
import { DeleteTimeLogSuccessModal } from './DeleteTimeLogModal/DeleteTimeLogSuccessModal'
import styles from './TimeLogsTable.module.scss'
import { useExpandTimeLogRewards } from './useExpandTimeLogRewards'

const formatVolunteeringTime = (timeInMinutes: number, t: TFunction) => {
  if (timeInMinutes <= 0) {
    return `0${t('workplaceGiving.timeFormatting.minutesAbbrev')}`
  }

  const durationTime = dayJS.duration(timeInMinutes, 'minutes')
  const days = durationTime.days() ? `${durationTime.days()}${t('workplaceGiving.timeFormatting.daysAbbrev')}` : ''
  const hours = durationTime.hours() ? `${durationTime.hours()}${t('workplaceGiving.timeFormatting.hoursAbbrev')}` : ''
  const minutes = durationTime.minutes()
    ? `${durationTime.minutes()}${t('workplaceGiving.timeFormatting.minutesAbbrev')}`
    : ''

  return `${days}${(days && hours) || (days && minutes) ? ' ' : ''}${hours}${hours && minutes ? ' ' : ''}${minutes}`
}

const formatTimeLogType = (type: VolunteeringTimeLog['type'], t: TFunction) => t(`typography.timeLogType.${type}`)

const sumContributed = (rewards: readonly VolunteeringTimeLogReward[] = []): Money | null =>
  rewards.length > 0
    ? {
        amount: rewards.reduce((total, { contributedAmount }) => total + contributedAmount.amount, 0),
        currency: rewards[0].contributedAmount.currency
      }
    : null

export function TimeLogsTable() {
  const { t } = useTranslation()
  const { isLoading, expandRewards } = useExpandTimeLogRewards()

  return isLoading ? (
    <Spinner size={8} aria-label={t('status.loading')} />
  ) : (
    <TimeLogsTableContent showRewards={expandRewards} />
  )
}

function TimeLogsTableContent({ showRewards }: Readonly<{ showRewards: boolean }>) {
  const {
    t,
    i18n: { language }
  } = useTranslation()
  const { workplaceGivingService } = useServices()
  const baseParams = useMemo<GetVolunteeringTimeLogsListProps>(
    () => ({ pageSize: 10, status: ['approved'], ...(showRewards && { expand: 'rewards' as const }) }),
    [showRewards]
  )

  const [{ data, isLoading, errorMessage, totalResults }, { nextPage, previousPage, query: refetchTimeLogs, refresh }] =
    useQueryList(workplaceGivingService.getVolunteeringTimeLogsList, baseParams)

  const refreshRef = useRef(refresh)
  useEffect(() => {
    refreshRef.current = refresh
  }, [refresh])

  const [selectedTimeLog, setSelectedTimeLog] = useState<{ id: string; employee: string } | null>(null)
  const [deleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false)
  const [deleteErrorModalOpen, setDeleteErrorModalOpen] = useState(false)
  const [deleteSuccessEmployee, setDeleteSuccessEmployee] = useState('')

  const [{ isLoading: isDeleteLoading }, { apiFunc: deleteTimeLog }] = useMutation(
    workplaceGivingService.deleteVolunteeringTimeLog,
    () => {
      setDeleteSuccessEmployee(selectedTimeLog?.employee ?? '')
      setSelectedTimeLog(null)
      setDeleteSuccessModalOpen(true)
      refreshRef.current()
    },
    () => {
      setSelectedTimeLog(null)
      setDeleteErrorModalOpen(true)
    }
  )

  const handleFilterChange = useCallback(
    (filter: {
      opportunityId?: string
      accountId?: string
      type?: VolunteeringTimeLogType
      dateFrom?: string
      dateTo?: string
    }) => {
      refetchTimeLogs({
        ...baseParams,
        opportunityId: filter.opportunityId,
        accountId: filter.accountId,
        ...(filter.type && { type: filter.type }),
        ...(filter.dateFrom && { dateFrom: filter.dateFrom }),
        ...(filter.dateTo && { dateTo: filter.dateTo })
      })
    },
    [refetchTimeLogs, baseParams]
  )

  const formatReward = (rewards?: VolunteeringTimeLogReward[]) => {
    const reward = sumContributed(rewards)

    return reward && reward.amount > 0
      ? formatMoney(reward, { locale: language, hideDecimalsIfZero: true })
      : t('status.notAvailable')
  }

  const columns = useMemo(
    () => [
      { id: 'employee', content: t('table.header.employee'), props: { width: '20%' } },
      { id: 'opportunity', content: t('table.header.opportunity'), props: { width: '25%' } },
      { id: 'hours', content: t('table.header.hours'), props: { width: '14%' } },
      ...(showRewards
        ? [{ id: 'volunteeringReward', content: t('table.header.volunteeringReward'), props: { width: '14%' } }]
        : []),
      { id: 'type', content: t('table.header.type'), props: { width: '16%' } },
      { id: 'date', content: t('table.header.date'), props: { width: '20%' } },
      { id: '', props: { width: '5%' } }
    ],
    [t, showRewards]
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={totalResults}
        isLoading={isLoading || !data}
        illustration="no-results"
        emptyTableTitle={t('typography.noTimeLogsTitle')}
        emptyTableDescription={t('typography.noTimeLogsDescription')}
      >
        <TimeLogsSearchAndFilters onFilterChange={handleFilterChange} />
        <Table>
          <TableBody>
            {data?.map(timeLog => (
              <TableRow key={timeLog.id}>
                <TableCell>{timeLog.employee}</TableCell>
                <TableCell>
                  {timeLog.type === 'on_platform' && timeLog.opportunity && timeLog.opportunityId ? (
                    <a
                      className={styles.opportunityLink}
                      href={`${env.NX_PUBLIC_WPG_ADDRESS}/volunteer/opportunities/${timeLog.opportunityId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {timeLog.opportunity}
                    </a>
                  ) : (
                    t('status.notAvailable')
                  )}
                </TableCell>
                <TableCell>
                  {formatVolunteeringTime(dayJS(timeLog.endTime).diff(timeLog.startTime, 'minute'), t)}
                </TableCell>
                {showRewards ? <TableCell>{formatReward(timeLog.rewards)}</TableCell> : null}
                <TableCell>
                  <Badge variant={timeLog.type === 'on_platform' ? 'interactive' : 'default'}>
                    {formatTimeLogType(timeLog.type, t)}
                  </Badge>
                </TableCell>
                <TableCell>{dayJS(timeLog.startTime).format('D MMM YYYY')}</TableCell>
                <TableCell>
                  <Menu
                    title={<Icon name="menu-vertical" size={6} />}
                    onSelect={(key: string) => {
                      if (key === 'delete') {
                        setSelectedTimeLog({ id: timeLog.id, employee: timeLog.employee })
                      }
                    }}
                    sections={[{ items: [{ key: 'delete', label: t('button.delete') }] }]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults ?? 0 })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>
      {selectedTimeLog && (
        <DeleteTimeLogModal
          open={!!selectedTimeLog}
          handleClose={() => setSelectedTimeLog(null)}
          handleDelete={() => deleteTimeLog(selectedTimeLog.id)}
          isLoading={isDeleteLoading}
          employeeName={selectedTimeLog.employee}
        />
      )}
      <DeleteTimeLogSuccessModal
        open={deleteSuccessModalOpen}
        handleClose={() => setDeleteSuccessModalOpen(false)}
        employeeName={deleteSuccessEmployee}
      />
      <DeleteTimeLogErrorModal open={deleteErrorModalOpen} handleClose={() => setDeleteErrorModalOpen(false)} />
    </>
  )
}
